import { useState, useEffect, useMemo } from "react";

export const useTypewriter = (
    text: string | string[],
    speed: number = 50,
    delay: number = 0,
    deleteSpeed: number = 30,
    pauseTime: number = 2000
) => {
    const [displayText, setDisplayText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentCharIndex, setCurrentCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isStarted, setIsStarted] = useState(false);

    // To prevent infinite re-renders if an array literal is passed, we stringify it for the dependency array
    const textString = JSON.stringify(text);
    const texts = useMemo(() => {
        const parsed = JSON.parse(textString);
        return Array.isArray(parsed) ? parsed : [parsed];
    }, [textString]);

    useEffect(() => {
        // Delay the start of typing if needed
        const timeout = setTimeout(() => {
            setIsStarted(true);
        }, delay);
        return () => clearTimeout(timeout);
    }, [delay]);

    useEffect(() => {
        if (!isStarted) return;

        const currentText = texts[currentIndex];

        if (isDeleting) {
            if (currentCharIndex > 0) {
                const timeout = setTimeout(() => {
                    setDisplayText(currentText.substring(0, currentCharIndex - 1));
                    setCurrentCharIndex(prev => prev - 1);
                }, deleteSpeed);
                return () => clearTimeout(timeout);
            } else {
                if (texts.length > 1) {
                    setIsDeleting(false);
                    setCurrentIndex((prev) => (prev + 1) % texts.length);
                }
            }
        } else {
            if (currentCharIndex < currentText.length) {
                const timeout = setTimeout(() => {
                    setDisplayText(currentText.substring(0, currentCharIndex + 1));
                    setCurrentCharIndex(prev => prev + 1);
                }, speed);
                return () => clearTimeout(timeout);
            } else {
                if (texts.length > 1) {
                    const timeout = setTimeout(() => {
                        setIsDeleting(true);
                    }, pauseTime);
                    return () => clearTimeout(timeout);
                }
            }
        }
    }, [currentCharIndex, isDeleting, isStarted, speed, deleteSpeed, pauseTime, currentIndex, texts]);

    return displayText;
}
